import { API, ApiArgs } from '@/api/API'
import type { EventDetails } from '@/api/Helpers'
import type { MomentInterval } from '@/helpers/Intervals'
import { handleUnlimitedCapacity, normalizeSessions } from '@/helpers/SessionHelpers'
import { quantitiesPayload } from '@/helpers/TicketTypeQuantities'
import moment, { Moment } from 'moment-timezone'

export interface Session extends EventSession {
  startMoment: Moment
  endMoment: Moment
  availableCapacity: number
  priceIncrease?: number
}

export function fetchFirstAvailableSession(event: EventDetails, tgIds?: string[]) {
  const args = {
    query: {
      sold_out: 'F',
      _sort: 'start_datetime',
    },
    limit: 1,
  }

  if (tgIds) {
    args.query['ticket_group_id._in'] = tgIds.join(',')
  }

  return fetchSession(event, args)
}

function fetchSession(event: EventDetails, args?: ApiArgs): Promise<Session | null> {
  return (
    fetchSessions(event, args)
      // TODO Remove this guard.
      .then((sessions) => {
        if (sessions.length > 1) {
          throw new Error('Only one session was expected, but API returned multiple.')
        }
        return sessions
      })
      .then((sessions) => (sessions ? sessions[0] : null))
  )
}

function fetchSessions(event: EventDetails, args?: ApiArgs): Promise<Session[]> {
  const id = event.id
  const timezone = event.venue.timezone

  return API.getCached(`events/${id}/sessions`, args).then((response) => {
    return response.event_session!._data.map((session) => {
      const { capacity, used_capacity, end_datetime, start_datetime } = session
      return {
        ...session,
        startMoment: moment(start_datetime).tz(timezone),
        endMoment: moment(end_datetime).tz(timezone),
        availableCapacity: handleUnlimitedCapacity(capacity) - used_capacity,
      }
    })
  })
}

export function fetchSessionsOnDate(event: EventDetails, date: Moment, tgIds: string[]): Promise<Session[]> {
  return fetchNormalizedSessions(event, {
    _ondate: date.format('YYYY-MM-DD'),
    'ticket_group.id._in': tgIds.join(','),
  })
}

export function fetchSessionsBetween(
  event: EventDetails,
  { start, end }: MomentInterval,
  tgIds: string[],
): Promise<Session[]> {
  return fetchNormalizedSessions(event, {
    'start_datetime._gte': start.toISOString(),
    'start_datetime._lt': end.toISOString(),
    'ticket_group.id._in': tgIds.join(','),
  })
}

function fetchNormalizedSessions(event: EventDetails, params: Dictionary): Promise<Session[]> {
  return API.getCached<'event_session'>(`events/${event.id}/sessions`, { query: params }).then((response) =>
    normalizeSessions(response, event.venue),
  )
}

export function fetchSessionsForDateForMinimumQuantities(
  eventId: String,
  venue: Venue,
  date: Moment,
  quantities: TicketTypeQuantities,
): Promise<{
  sessions: Session[]
  priceSchedules: PriceSchedule[]
}> {
  const args = {
    body: quantitiesPayload(quantities),
    query: {
      _ondate: date.format('YYYY-MM-DD'),
      _sort: 'start_datetime',
      _include_sold_out: true,
    },
  }

  const url = `events/${eventId}/sessions`
  return API.post<'event_session' | 'price_schedule'>(url, args).then((response) => {
    return {
      sessions: normalizeSessions(response, venue),
      priceSchedules: response.price_schedule._data,
    }
  })
}
